// src/features/invoices/components/EbicsBankverbindung.tsx
//
// Bereich "Bankverbindung (EBICS)" im Zahlungsabgleich (XTAX GmbH & Co. KG,
// Bankumsatzabruf Kreissparkasse Köln, 04.11.2026). Sichtbar nur in der
// Ansicht von Tim Wolter; die Einbindung prüft das im PaymentReconciliationView.
//
// Stufe 1: Teilnehmerdaten anzeigen, Schlüssel einmalig erzeugen und mit INI
// und HIA an die Bank senden, Initialisierungsbriefe als PDF herunterladen.
// Stufe 2: Nach der Freischaltung durch die Bank die Bankschlüssel abholen und
// gegen das Bankparameterblatt prüfen.
// Stufe 3: Anzeige des täglichen Abrufs (werktags 6:00 Uhr) und Abruf von Hand.

import React, { useState, useEffect, useCallback } from 'react';
import { Landmark, Loader, Check, AlertTriangle, KeyRound, FileDown, RefreshCw, ShieldCheck, Download } from 'lucide-react';
import { useDialog } from '../../../shared/components/DialogProvider';
import { ebicsVerbindungService, EbicsTeilnehmer } from '../../../services/payment/ebicsVerbindungService';

const FAX_NUMMER = '0221 227-9171';

const formatDateTime = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatIban = (iban: string): string => iban.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();

const STATUS_TEXT: Record<string, { label: string; color: string; bg: string; border: string }> = {
  neu: { label: 'Noch nicht eingerichtet', color: '#6B7280', bg: '#F3F4F6', border: '#E5E7EB' },
  schluessel_erzeugt: { label: 'Unterbrochen: Schlüssel erzeugt, noch nicht gesendet', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  ini_gesendet: { label: 'Unterbrochen: INI gesendet, HIA offen', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
  initialisiert: { label: 'Schlüssel bei der Bank, Freischaltung durch die Bank offen', color: '#2563EB', bg: '#EFF6FF', border: '#BFDBFE' },
  bereit: { label: 'Verbunden, täglicher Abruf aktiv', color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' }
};

const knopfStil = (gesperrt: boolean, primaer: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  backgroundColor: gesperrt ? '#9CA3AF' : (primaer ? '#2563EB' : '#F3F4F6'),
  color: gesperrt || primaer ? 'white' : '#374151',
  border: primaer || gesperrt ? 'none' : '1px solid #D1D5DB',
  borderRadius: '6px',
  cursor: gesperrt ? 'not-allowed' : 'pointer',
  fontSize: '14px', fontWeight: primaer ? '600' : '500',
  display: 'flex', alignItems: 'center', gap: '6px'
});

export const EbicsBankverbindung: React.FC = () => {
  const { confirm: dialogConfirm, notify: dialogNotify } = useDialog();

  const [teilnehmer, setTeilnehmer] = useState<EbicsTeilnehmer | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [vorgang, setVorgang] = useState<'initialisieren' | 'briefe' | 'bankschluessel' | 'abrufen' | null>(null);

  const ladeStatus = useCallback(async () => {
    setLaedt(true);
    try {
      const ergebnis = await ebicsVerbindungService.ladeStatus();
      if (ergebnis.success && ergebnis.teilnehmer) {
        setTeilnehmer(ergebnis.teilnehmer);
        setLadeFehler(null);
      } else {
        setLadeFehler(ergebnis.error || 'Status konnte nicht geladen werden');
      }
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    ladeStatus();
  }, [ladeStatus]);

  const handleInitialisieren = async () => {
    if (!teilnehmer) return;
    const fortsetzen = teilnehmer.status !== 'neu';
    const bestaetigt = await dialogConfirm({
      titel: fortsetzen ? 'Initialisierung fortsetzen' : 'Schlüssel erzeugen und an die Bank senden',
      text: fortsetzen
        ? 'Der letzte Lauf wurde unterbrochen. Es werden dieselben Schlüssel verwendet und nur die noch offenen Aufträge an die Bank gesendet.'
        : `Es werden jetzt die drei EBICS-Schlüssel für den Teilnehmer ${teilnehmer.user_id} erzeugt und an die ${teilnehmer.bank_name} gesendet. Das geht nur ein einziges Mal. Danach lädst Du die Initialisierungsbriefe herunter, unterschreibst sie und faxt sie mit dem Antwortbogen an ${FAX_NUMMER}.`,
      bestaetigenLabel: fortsetzen ? 'Fortsetzen' : 'Erzeugen und senden'
    });
    if (!bestaetigt) return;

    setVorgang('initialisieren');
    try {
      const ergebnis = await ebicsVerbindungService.initialisiere();
      if (ergebnis.teilnehmer) setTeilnehmer(ergebnis.teilnehmer);
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Initialisierung nicht abgeschlossen', text: ergebnis.error || 'Unbekannter Fehler', variante: 'gefahr' });
        return;
      }
      await dialogNotify({
        titel: 'Schlüssel an die Bank gesendet',
        text: `INI und HIA wurden von der Bank angenommen. Bitte jetzt die Initialisierungsbriefe herunterladen, beide unterschreiben und mit dem Antwortbogen an ${FAX_NUMMER} faxen.`
      });
    } finally {
      setVorgang(null);
    }
  };

  const handleBriefe = async () => {
    setVorgang('briefe');
    try {
      const ergebnis = await ebicsVerbindungService.ladeBriefeHerunter();
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Briefe nicht verfügbar', text: ergebnis.error || 'Unbekannter Fehler', variante: 'gefahr' });
      }
    } finally {
      setVorgang(null);
    }
  };

  const handleBankschluessel = async () => {
    setVorgang('bankschluessel');
    try {
      const ergebnis = await ebicsVerbindungService.holeBankschluessel();
      if (ergebnis.teilnehmer) setTeilnehmer(ergebnis.teilnehmer);
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Bankschlüssel nicht übernommen', text: ergebnis.error || 'Unbekannter Fehler', variante: 'warnung' });
        return;
      }
      await dialogNotify({
        titel: 'Bankschlüssel geprüft',
        text: 'Die Bankschlüssel stimmen mit dem Bankparameterblatt überein. Ab jetzt werden die Tagesauszüge werktags um 6:00 Uhr automatisch abgerufen und zugeordnet.'
      });
    } finally {
      setVorgang(null);
    }
  };

  const handleAbrufen = async () => {
    setVorgang('abrufen');
    try {
      const ergebnis = await ebicsVerbindungService.rufeTagesauszuegeAb();
      if (ergebnis.teilnehmer) setTeilnehmer(ergebnis.teilnehmer);
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Abruf fehlgeschlagen', text: ergebnis.error || 'Unbekannter Fehler', variante: 'gefahr' });
        return;
      }
      await dialogNotify({ titel: 'Abruf abgeschlossen', text: ergebnis.meldung || 'Abruf abgeschlossen.' });
    } finally {
      setVorgang(null);
    }
  };

  const statusCfg = teilnehmer ? (STATUS_TEXT[teilnehmer.status] || STATUS_TEXT.neu) : null;
  const beschaeftigt = vorgang !== null || (teilnehmer?.laeuft_gerade ?? false);
  const drehen = { animation: 'spin 1s linear infinite' };

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB',
      padding: '20px', marginBottom: '20px'
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '15px', fontWeight: '600', color: '#111827' }}>
          <Landmark size={16} color="#2563EB" />
          Bankverbindung (EBICS)
        </div>
        <button
          onClick={ladeStatus}
          disabled={laedt || beschaeftigt}
          title="Status neu laden"
          style={{
            background: 'none', border: 'none', padding: '4px',
            cursor: laedt || beschaeftigt ? 'not-allowed' : 'pointer', color: '#6B7280'
          }}
        >
          <RefreshCw size={15} style={laedt ? drehen : undefined} />
        </button>
      </div>
      <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
        Automatischer Abruf der Kontoumsätze. Dieser Bereich ist nur in Deiner Ansicht sichtbar.
      </div>

      {ladeFehler && (
        <div style={{
          padding: '10px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
          borderRadius: '6px', fontSize: '13px', color: '#991B1B'
        }}>
          {ladeFehler}
        </div>
      )}

      {!teilnehmer && !ladeFehler && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6B7280', fontSize: '14px' }}>
          <Loader size={16} style={drehen} />
          Status wird geladen ...
        </div>
      )}

      {teilnehmer && statusCfg && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 16px',
            fontSize: '13px', color: '#111827', marginBottom: '14px'
          }}>
            <span style={{ color: '#6B7280' }}>Bank</span><span>{teilnehmer.bank_name}</span>
            <span style={{ color: '#6B7280' }}>Konto</span><span>{teilnehmer.kontoinhaber}, {formatIban(teilnehmer.iban)}</span>
            <span style={{ color: '#6B7280' }}>Host-ID</span><span>{teilnehmer.host_id}</span>
            <span style={{ color: '#6B7280' }}>Kunden-ID</span><span>{teilnehmer.partner_id}</span>
            <span style={{ color: '#6B7280' }}>Teilnehmer-ID</span><span>{teilnehmer.user_id} ({teilnehmer.teilnehmer_name})</span>
            {teilnehmer.ini_gesendet_am && (
              <><span style={{ color: '#6B7280' }}>INI gesendet</span><span>{formatDateTime(teilnehmer.ini_gesendet_am)}{teilnehmer.ini_auftragsnummer ? `, Auftrag ${teilnehmer.ini_auftragsnummer}` : ''}</span></>
            )}
            {teilnehmer.hia_gesendet_am && (
              <><span style={{ color: '#6B7280' }}>HIA gesendet</span><span>{formatDateTime(teilnehmer.hia_gesendet_am)}{teilnehmer.hia_auftragsnummer ? `, Auftrag ${teilnehmer.hia_auftragsnummer}` : ''}</span></>
            )}
            {teilnehmer.bankschluessel_geprueft_am && (
              <><span style={{ color: '#6B7280' }}>Bankschlüssel geprüft</span><span>{formatDateTime(teilnehmer.bankschluessel_geprueft_am)}</span></>
            )}
            {teilnehmer.status === 'bereit' && (
              <>
                <span style={{ color: '#6B7280' }}>Automatischer Abruf</span><span>werktags um 6:00 Uhr</span>
                <span style={{ color: '#6B7280' }}>Letzter Abruf</span>
                <span>{teilnehmer.letzter_abruf_am ? `${formatDateTime(teilnehmer.letzter_abruf_am)}: ${teilnehmer.letzter_abruf_ergebnis || '-'}` : 'noch keiner'}</span>
              </>
            )}
          </div>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
            backgroundColor: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.border}`,
            marginBottom: '14px'
          }}>
            {teilnehmer.status === 'bereit' ? <ShieldCheck size={13} /> : (teilnehmer.status === 'initialisiert' ? <Check size={13} /> : <KeyRound size={13} />)}
            {statusCfg.label}
          </div>

          {teilnehmer.letzter_fehler && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              padding: '10px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: '6px', fontSize: '13px', color: '#991B1B', marginBottom: '14px', lineHeight: '1.5'
            }}>
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>{teilnehmer.letzter_fehler}</span>
            </div>
          )}

          {teilnehmer.status === 'bereit' ? (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={handleAbrufen} disabled={beschaeftigt} style={knopfStil(beschaeftigt, true)}>
                {vorgang === 'abrufen' ? <Loader size={15} style={drehen} /> : <Download size={15} />}
                {vorgang === 'abrufen' ? 'Tagesauszüge werden abgerufen ...' : 'Jetzt abrufen'}
              </button>
              <button onClick={handleBriefe} disabled={beschaeftigt} style={knopfStil(beschaeftigt, false)}>
                {vorgang === 'briefe' ? <Loader size={15} style={drehen} /> : <FileDown size={15} />}
                Initialisierungsbriefe
              </button>
            </div>
          ) : teilnehmer.status === 'initialisiert' ? (
            <div>
              <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '12px' }}>
                Initialisierungsbriefe herunterladen, beide Seiten unterschreiben und zusammen mit dem
                Antwortbogen der Bank an {FAX_NUMMER} faxen. Sobald die Bank den Zugang freigeschaltet hat,
                die Bankschlüssel abholen. Sie werden gegen das Bankparameterblatt geprüft; erst danach
                startet der tägliche Abruf.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={handleBankschluessel} disabled={beschaeftigt} style={knopfStil(beschaeftigt, true)}>
                  {vorgang === 'bankschluessel' ? <Loader size={15} style={drehen} /> : <ShieldCheck size={15} />}
                  {vorgang === 'bankschluessel' ? 'Bankschlüssel werden geprüft ...' : 'Bankschlüssel abholen und prüfen'}
                </button>
                <button onClick={handleBriefe} disabled={beschaeftigt} style={knopfStil(beschaeftigt, false)}>
                  {vorgang === 'briefe' ? <Loader size={15} style={drehen} /> : <FileDown size={15} />}
                  {vorgang === 'briefe' ? 'PDF wird erstellt ...' : 'Initialisierungsbriefe herunterladen'}
                </button>
              </div>
            </div>
          ) : (
            <button onClick={handleInitialisieren} disabled={beschaeftigt} style={knopfStil(beschaeftigt, true)}>
              {vorgang === 'initialisieren' ? <Loader size={15} style={drehen} /> : <KeyRound size={15} />}
              {vorgang === 'initialisieren'
                ? 'Wird an die Bank gesendet ...'
                : (teilnehmer.status === 'neu' ? 'Schlüssel erzeugen und an die Bank senden' : 'Initialisierung fortsetzen')}
            </button>
          )}
        </>
      )}
    </div>
  );
};
